/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import ArgosFlexbox from './Containers/ArgosFlexbox';
import LinearProgress, { LinearProgressProps } from '@mui/material/LinearProgress';
import React from "react";
import {theme} from '../../constants/theme';
import Typography from '@mui/material/Typography';


/**
 * Normalizes a value within a specified range. Used with LinearProgress because it
 * expects a value between 0 and 100.
 *
 * @param {number} value - The value to be normalized.
 * @param {number} min - The minimum value of the range. Default is 0.
 * @param {number} max - The maximum value of the range. Default is 100.
 * @returns {number} The normalized value.
 */
export function normalize(value: number, min: number = 0, max: number = 100): number {
  return ((value - min) * 100) / (max - min)
}


/**
 * Renders a linear progress bar with a label.
 *
 * @param {LinearProgressProps} props - The props for the LinearProgress component.
 * @param {number} props.value - The value of the progress bar (between 0 and 100). Use normalize() above to convert if needed.
 * @returns {JSX.Element} The rendered LinearProgressWithLabel component.
 */
export default function LinearProgressWithLabel(props: LinearProgressProps & {percentage: number, currentIteration: number, totalIterations: number}): JSX.Element {
  return (
    <ArgosFlexbox vertical style={{justifyContent: "center", alignItems: 'center'}}>
      <Typography variant="body2" color="textSecondary" style={{fontSize: "16px", color: theme.layout.header.backgroundColor}}>
        {`${props.currentIteration} / ${props.totalIterations}`}
      </Typography>
      <ArgosFlexbox style={{justifyContent: "left", alignItems: 'center'}}>
        <LinearProgress variant="determinate" {...props} />
        <ArgosFlexbox style={{minWidth: 35}}>
          <Typography variant="body2" color="textSecondary" style={{fontSize: "16px", color: theme.layout.header.backgroundColor}}>
            {`${Math.round(props.percentage)}%`}
          </Typography>
        </ArgosFlexbox>
      </ArgosFlexbox>
    </ArgosFlexbox>
  )
}
